import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { environment } from 'src/environments/environment';

export type NotificationInboxFilter = 'all' | 'unread' | 'read';

export interface InboxNotification {
  id: number;
  kind: string;
  title: string;
  body: string;
  overlay: string | null;
  url: string | null;
  entityId: number | null;
  createdAt: string;
  readAt: string | null;
}

export interface NotificationInboxPage {
  items: InboxNotification[];
  total: number;
  unreadCount: number;
  pageIndex: number;
  pageSize: number;
}

@Injectable({ providedIn: 'root' })
export class NotificationInboxService {
  private readonly endpoint = `${environment.endpoint}collaboration/notifications/inbox`;
  private readonly unreadCountState = signal(0);
  readonly unreadCount = this.unreadCountState.asReadonly();

  constructor(private readonly http: HttpClient) {}

  list(pageIndex = 0, pageSize = 30, filter: NotificationInboxFilter = 'all'): Observable<NotificationInboxPage> {
    const params = new HttpParams()
      .set('pageIndex', String(pageIndex))
      .set('pageSize', String(pageSize))
      .set('filter', filter);
    return this.http.get<NotificationInboxPage>(this.endpoint, { params }).pipe(
      tap(page => this.unreadCountState.set(Math.max(0, Number(page?.unreadCount || 0)))),
    );
  }

  refreshUnreadCount(): Observable<number> {
    return this.http.get<{ count?: number }>(`${this.endpoint}/unread-count`).pipe(
      map(response => Math.max(0, Number(response?.count || 0))),
      tap(count => this.unreadCountState.set(count)),
      catchError(() => of(this.unreadCount())),
    );
  }

  setReadState(notificationId: number, read: boolean): Observable<{ notificationId: number; read: boolean; unreadCount: number }> {
    return this.http.patch<{ notificationId: number; read: boolean; unreadCount: number }>(
      `${this.endpoint}/${notificationId}/read`,
      { read },
    ).pipe(
      tap(response => this.unreadCountState.set(Math.max(0, Number(response?.unreadCount || 0)))),
    );
  }

  markAllAsRead(): Observable<{ updatedCount: number; unreadCount: number }> {
    return this.http.patch<{ updatedCount: number; unreadCount: number }>(
      `${this.endpoint}/read-all`,
      {},
    ).pipe(
      tap(response => this.unreadCountState.set(Math.max(0, Number(response?.unreadCount || 0)))),
    );
  }

  reset(): void {
    this.unreadCountState.set(0);
  }
}
